import {
  ACCESS_STATUS_LABEL,
  PLATFORMS,
  cleanHandleInput,
  displayUrl,
  formatPhone,
  isEmpty,
  openableUrl,
  platformUrl,
  promotionWhen,
  scheduleText,
  type AccessStatus,
  type Field,
  type Leaf,
  type PlatformKey,
  type PortalConfig,
  type Promotion,
  type Schedule,
  type SectionDocs,
  type SectionId,
} from '@sonder/shared'
import { Link } from 'react-router'
import { enabledSections, useProgress } from '@/data/client'
import { reviewByline, useNameOf } from '@/data/review'
import { BlockHead } from '@/sections/common'
import { Icons, PlatformIcon, StatusPill, type PillTone } from '@/ui'
import { answerFlag, reviewAnchor, type ReviewRow } from './reviewRows'

type Props = {
  config: PortalConfig
  docs: Partial<SectionDocs>
  rows: ReviewRow[]
  brandCount?: number
  /** Where each step's Edit link goes. */
  editHref: (id: SectionId) => string
  /** Staff view: shows who edited what. */
  team?: boolean
  /** Shown when `rows` is filtered down to nothing. */
  empty?: string
  /** Keep steps with nothing in them, so every step on the list above has somewhere to land. */
  showEmptySteps?: boolean
}

/**
 * The answers, step by step: each step's heading sticks under the top bar while its answers scroll past, then
 * the next step's takes over. Inside a step, answers are grouped by what they belong to (a brand, a location,
 * a platform), each question with its answer beneath it.
 */
export function ReviewAnswers({ config, docs, rows, brandCount, editHref, team = false, empty = 'Nothing to show.', showEmptySteps = false }: Props) {
  const progress = useProgress(config, docs)
  const nameOf = useNameOf()
  const steps = enabledSections(config, brandCount)
    .map((s) => ({ ...s, rows: rows.filter((r) => r.section === s.id) }))
    .filter((s) => showEmptySteps || s.rows.length > 0)
  if (steps.length === 0) return <p className="border-y border-gray-200 py-8 text-small text-dim">{empty}</p>
  return (
    <div className="grid gap-16">
      {steps.map((s) => {
        const p = progress?.sections[s.id]
        const state = progress?.states[s.id] ?? 'todo'
        return (
          <section key={s.id} id={reviewAnchor(s.id)} aria-label={s.title} className="grid scroll-mt-[var(--topbar-h,4rem)] gap-8 border-t border-ink pt-6">
            <BlockHead
              eyebrow={`Step ${s.number}`}
              title={s.title}
              aside={
                <div className="flex items-center gap-4">
                  {p && <StepPill state={state} unconfirmed={p.unconfirmed} optional={s.optional} />}
                  <Link to={editHref(s.id as SectionId)} className="text-[0.75rem] uppercase tracking-eyebrow text-ink underline underline-offset-4 hover:text-charcoal">
                    Edit
                  </Link>
                </div>
              }
            />
            {s.rows.length === 0 && <p className="text-small text-dim">Nothing added yet.</p>}
            {groupRows(s.rows).map((g) => (
              <div key={g.where ?? ''} className="grid gap-1">
                {/* On wide screens it sticks just under the step's heading, so a long brand still says whose answers these are. */}
                {g.where && <h3 className="z-[5] bg-paper/95 py-2 text-[0.75rem] font-medium uppercase tracking-eyebrow text-ink backdrop-blur lg:sticky lg:top-[calc(var(--topbar-h,4rem)+5.25rem)]">{g.where}</h3>}
                <dl className="divide-y divide-gray-200 border-y border-gray-200">
                  {g.rows.map((r) => {
                    const fl = answerFlag(r, team)
                    return (
                      <div key={r.path} className="grid gap-x-6 gap-y-2 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                        <div className="grid min-w-0 gap-1">
                          <dt className="text-small text-dim">
                            {r.label}
                            {r.detail && <span className="text-charcoal"> · {r.detail}</span>}
                          </dt>
                          <dd className="min-w-0 text-body text-ink">
                            <AnswerValue leaf={r} />
                            {reviewByline(r.field, nameOf) && <p className="mt-1 text-small text-dim">{reviewByline(r.field, nameOf)}</p>}
                          </dd>
                        </div>
                        {fl && <StatusPill tone={fl.tone} className="justify-self-start sm:justify-self-end">{fl.label}</StatusPill>}
                      </div>
                    )
                  })}
                </dl>
              </div>
            ))}
          </section>
        )
      })}
    </div>
  )
}

/** The step's overall state, worded as on the step list. */
export function StepPill({ state, unconfirmed, optional }: { state: string; unconfirmed: number; optional: boolean }) {
  const tone: PillTone = state === 'done' ? 'ok' : state === 'review' ? 'warn' : state === 'partial' ? 'ink' : 'neutral'
  const label = state === 'done' ? 'Complete' : state === 'review' ? `${unconfirmed} to confirm` : state === 'partial' ? 'In progress' : optional ? 'Optional' : 'Not started'
  return <StatusPill tone={tone}>{label}</StatusPill>
}

/** Answers grouped by the brand, location or platform they belong to, in page order. */
function groupRows(rows: ReviewRow[]): Array<{ where?: string; rows: ReviewRow[] }> {
  const groups = new Map<string, { where?: string; rows: ReviewRow[] }>()
  for (const r of rows) {
    const key = r.where ?? ''
    if (!groups.has(key)) groups.set(key, { where: r.where, rows: [] })
    groups.get(key)!.rows.push(r)
  }
  // FAQ questions follow the question order rather than the order they were answered in.
  for (const g of groups.values()) g.rows.sort((a, b) => a.order - b.order)
  return [...groups.values()]
}

function AnswerValue({ leaf }: { leaf: Leaf }) {
  const f = leaf.field as Field<unknown> | undefined
  if (!f || isEmpty(f.value)) return <span className="text-dim">{leaf.byClient ? 'Not started' : 'Not answered'}</span>
  const v = f.value
  if (leaf.platform && leaf.path.endsWith('.handle')) return <PlatformHandle platform={leaf.platform} value={String(v)} />
  switch (leaf.kind) {
    case 'schedule':
      return (
        <ul className="grid gap-2">
          {(v as Schedule[]).map((s) => (
            <li key={s.id}>
              <span className="font-medium">{s.label}</span>
              <span className="block text-charcoal">
                {scheduleText(s)
                  .split(' · ')
                  .map((line) => (
                    <span key={line} className="block">{line}</span>
                  ))}
              </span>
            </li>
          ))}
        </ul>
      )
    case 'promotions':
      return (
        <ul className="grid gap-2">
          {(v as Promotion[]).map((p) => (
            <li key={p.id}>
              <span className="font-medium">{p.name || 'Untitled special'}</span>
              <span className="block text-small text-dim">{promotionWhen(p)}</span>
              {p.details && <span className="block whitespace-pre-wrap text-charcoal">{p.details}</span>}
            </li>
          ))}
        </ul>
      )
    case 'status':
      return <>{ACCESS_STATUS_LABEL[v as AccessStatus] ?? String(v)}</>
    case 'phone':
      return <>{formatPhone(String(v))}</>
    case 'url': {
      const href = openableUrl(String(v))
      if (!href) return <span className="break-words">{String(v)}</span>
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="break-words underline decoration-gray-300 underline-offset-4 hover:decoration-ink">
          {displayUrl(href)}
        </a>
      )
    }
    default:
      return <span className="whitespace-pre-wrap break-words">{typeof v === 'string' ? v : JSON.stringify(v)}</span>
  }
}

/** A handle as the Platforms step shows it: the platform's own prefix, linked to the page it opens. */
function PlatformHandle({ platform, value }: { platform: PlatformKey; value: string }) {
  const format = PLATFORMS[platform].handleFormat
  const href = platformUrl(platform, value)
  // Not an address yet (a team note like "Not found"): show it as written.
  if (!href) return <span className="whitespace-pre-wrap break-words">{value}</span>
  const shown = format.kind === 'username' ? `${format.prefix}${cleanHandleInput(platform, value)}` : displayUrl(href)
  return (
    <span className="grid gap-1">
      <a href={href} target="_blank" rel="noopener noreferrer" title={`Open ${displayUrl(href)}`} className="inline-flex min-w-0 items-center gap-2 justify-self-start underline decoration-gray-300 underline-offset-4 hover:decoration-ink">
        <PlatformIcon platform={platform} className="h-4 w-4" />
        <span className="break-all">{shown}</span>
        <Icons.ArrowUpRight size={12} className="shrink-0 text-dim" />
      </a>
      {/* An @ handle hides the address, so it is spelled out, as on the Platforms step. */}
      {format.kind === 'username' && format.prefix === '@' && <span className="text-small text-dim">{displayUrl(href)}</span>}
    </span>
  )
}
